// ═══════════════════════════════════════════════════════════
// Departments API
// ═══════════════════════════════════════════════════════════

import { fetchRecords, createRecord, updateRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

export interface Department {
  recordId: number;
  jurisdictionId: string;
  name: string;
  code: string;
  headUserId?: string;
  email?: string;
  isActive: boolean;
}

const F = FIELDS.departments;

function rowToDept(row: Record<string, unknown>): Department {
  return {
    recordId:       row['record_id'] as number,
    jurisdictionId: String(row[F.jurisdictionId] ?? ''),
    name:           String(row[F.name] ?? ''),
    code:           String(row[F.code] ?? ''),
    headUserId:     row[F.headUserId] as string | undefined,
    email:          row[F.email] as string | undefined,
    isActive:       row[F.isActive] === true || row[F.isActive] === 'true',
  };
}

let _cache: Department[] | null = null;

export async function listDepartments(opts?: { forceRefresh?: boolean }): Promise<Department[]> {
  if (_cache && !opts?.forceRefresh) return _cache;
  const { records } = await fetchRecords(TABLES.departments, { limit: 200 });
  _cache = (records as Record<string, unknown>[])
    .map(rowToDept)
    .filter(d => d.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));
  return _cache;
}

export async function createDepartment(input: {
  jurisdictionId: string;
  name: string;
  code?: string;
  email?: string;
  headUserId?: string;
  isActive?: boolean;
}): Promise<Department> {
  const row = await createRecord(TABLES.departments, {
    [F.jurisdictionId]: input.jurisdictionId,
    [F.name]:           input.name,
    [F.code]:           input.code ?? '',
    [F.email]:          input.email ?? '',
    [F.headUserId]:     input.headUserId ?? '',
    [F.isActive]:       input.isActive ?? true,
  });
  _cache = null; // invalidate cache
  return rowToDept(row as Record<string, unknown>);
}

export async function updateDepartment(
  recordId: number,
  patch: Partial<Omit<Department, 'recordId'>>,
): Promise<Department> {
  const p: Record<string, unknown> = {};
  if (patch.name          !== undefined) p[F.name]          = patch.name;
  if (patch.code          !== undefined) p[F.code]          = patch.code;
  if (patch.email         !== undefined) p[F.email]         = patch.email;
  if (patch.headUserId    !== undefined) p[F.headUserId]    = patch.headUserId;
  if (patch.isActive      !== undefined) p[F.isActive]      = patch.isActive;
  const row = await updateRecord(TABLES.departments, recordId, p);
  _cache = null;
  return rowToDept(row as Record<string, unknown>);
}

export async function deleteDepartment(recordId: number): Promise<void> {
  await deleteRecord(TABLES.departments, recordId);
  _cache = null;
}

export async function listDepartmentsAnon(): Promise<Department[]> {
  const { records } = await fetchRecords(TABLES.departments, { limit: 200, anonymous: true } as Parameters<typeof fetchRecords>[1]);
  return (records as Record<string, unknown>[])
    .map(rowToDept)
    .filter(d => d.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));
}
